import React from 'react';
import {
  Avatar,
  Badge,
  Box,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { MessageCircleMore, Search } from 'lucide-react';
import { TeamChatConversation } from '../../../utils/types';

export const teamChatAccent = '#1F7A5C';
export const teamChatSidebarBg = '#F8FAFB';
export const teamChatThreadBg = '#EFEAE2';
export const teamChatPanelBorder = 'gray.200';

export const formatChatTime = (value?: string | null) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

export const formatChatDayLabel = (value?: string | null) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (first: Date, second: Date) =>
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate();

  if (isSameDay(date, today)) {
    return 'Today';
  }

  if (isSameDay(date, yesterday)) {
    return 'Yesterday';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const getConversationLabel = (conversation: TeamChatConversation) =>
  conversation.recipient_name || conversation.name || 'Direct message';

export const getConversationPreview = (conversation: TeamChatConversation) => {
  const lastMessage = conversation.last_message;
  if (!lastMessage) {
    return 'No messages yet';
  }

  if (lastMessage.content?.trim()) {
    return lastMessage.content;
  }

  switch (lastMessage.message_type) {
    case 'image':
      return 'Photo';
    case 'file':
      return 'File';
    case 'system':
      return 'System update';
    default:
      return 'New message';
  }
};

export const TeamChatShell = ({
  sidebar,
  thread,
}: {
  sidebar: React.ReactNode;
  thread: React.ReactNode;
}) => (
  <Box bg="#EDEDED" minH="calc(100vh - 72px)" px={{ base: 0, lg: 4 }} py={{ base: 0, lg: 4 }}>
    <Box
      maxW="1480px"
      mx="auto"
      bg="white"
      borderRadius={{ base: '0', lg: '28px' }}
      overflow="hidden"
      borderWidth={{ base: '0', lg: '1px' }}
      borderColor={teamChatPanelBorder}
      boxShadow={{ base: 'none', lg: '0 18px 48px rgba(15, 23, 42, 0.08)' }}
      h={{ base: 'calc(100vh - 72px)', lg: 'calc(100vh - 104px)' }}
    >
      <HStack align="stretch" spacing={0} h="full">
        {sidebar}
        {thread}
      </HStack>
    </Box>
  </Box>
);

export const TeamChatSearchInput = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => (
  <InputGroup>
    <InputLeftElement pointerEvents="none">
      <Search size={16} color="#718096" />
    </InputLeftElement>
    <Input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      borderRadius="14px"
      bg="white"
      borderColor="gray.200"
      _focusVisible={{
        borderColor: 'green.400',
        boxShadow: '0 0 0 1px rgba(31, 122, 92, 0.35)',
      }}
    />
  </InputGroup>
);

export const TeamChatSidebar = ({
  header,
  search,
  conversations,
  activeConversationId,
  onSelectConversation,
  emptyState,
  loading,
  mobileHidden = false,
}: {
  header: React.ReactNode;
  search: React.ReactNode;
  conversations: TeamChatConversation[];
  activeConversationId?: string;
  onSelectConversation: (conversationId: string) => void;
  emptyState: React.ReactNode;
  loading?: boolean;
  mobileHidden?: boolean;
}) => (
  <Box
    w={{ base: mobileHidden ? '0' : '100%', md: '380px', xl: '410px' }}
    minW={{ md: '380px', xl: '410px' }}
    borderRightWidth={{ base: mobileHidden ? '0' : '0', md: '1px' }}
    borderColor={teamChatPanelBorder}
    bg={teamChatSidebarBg}
    display={{ base: mobileHidden ? 'none' : 'flex', md: 'flex' }}
    flexDirection="column"
    h="full"
  >
    {header}
    <Box px={4} py={3} borderBottomWidth="1px" borderColor="gray.200" bg="#F6F8F9">
      {search}
    </Box>

    <VStack spacing={0} align="stretch" overflowY="auto" flex={1} bg="white">
      {loading ? (
        Array.from({ length: 8 }).map((_, index) => (
          <HStack key={index} px={4} py={3} spacing={3} borderBottomWidth="1px" borderColor="gray.100">
            <Skeleton borderRadius="full" boxSize="48px" />
            <Box flex={1}>
              <Skeleton h="12px" mb={2} borderRadius="6px" />
              <Skeleton h="10px" w="75%" borderRadius="6px" />
            </Box>
          </HStack>
        ))
      ) : conversations.length === 0 ? (
        emptyState
      ) : (
        conversations.map((conversation) => {
          const active = activeConversationId === conversation.id;
          return (
            <HStack
              key={conversation.id}
              px={4}
              py={3}
              spacing={3}
              align="center"
              cursor="pointer"
              bg={active ? '#F0F7F4' : 'white'}
              borderLeftWidth="3px"
              borderLeftColor={active ? teamChatAccent : 'transparent'}
              _hover={{ bg: active ? '#F0F7F4' : '#F7FAFC' }}
              transition="background 0.16s ease"
              borderBottomWidth="1px"
              borderBottomColor="gray.100"
              onClick={() => onSelectConversation(conversation.id)}
            >
              <Avatar
                name={getConversationLabel(conversation)}
                size="md"
                bg={active ? teamChatAccent : 'gray.300'}
                color={active ? 'white' : 'gray.700'}
              />
              <Box flex={1} minW={0}>
                <HStack justify="space-between" align="baseline" mb={0.5}>
                  <Text fontWeight={active ? '800' : '700'} color="gray.900" noOfLines={1}>
                    {getConversationLabel(conversation)}
                  </Text>
                  <Text fontSize="xs" color="gray.500" flexShrink={0}>
                    {formatChatTime(conversation.last_message?.created_at || conversation.updated_at)}
                  </Text>
                </HStack>
                <HStack justify="space-between" align="center" spacing={3}>
                  <Text fontSize="sm" color="gray.500" noOfLines={1}>
                    {getConversationPreview(conversation)}
                  </Text>
                  <Badge
                    colorScheme={conversation.is_group ? 'purple' : 'green'}
                    borderRadius="999px"
                    px={2}
                    py={0.5}
                    fontSize="0.64rem"
                    fontWeight="700"
                  >
                    {conversation.is_group ? 'Group' : 'Direct'}
                  </Badge>
                </HStack>
              </Box>
            </HStack>
          );
        })
      )}
    </VStack>
  </Box>
);

export const TeamChatSidebarHeader = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) => (
  <HStack
    px={4}
    py={4}
    justify="space-between"
    align="center"
    borderBottomWidth="1px"
    borderColor="gray.200"
    bg="#F0F2F5"
  >
    <Box>
      <Text fontWeight="800" color="gray.900">
        {title}
      </Text>
      {subtitle ? (
        <Text fontSize="sm" color="gray.500">
          {subtitle}
        </Text>
      ) : null}
    </Box>
    {action}
  </HStack>
);

export const TeamChatSidebarEmpty = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <VStack py={14} px={6} spacing={3} textAlign="center">
    <Box
      boxSize="56px"
      borderRadius="full"
      bg="gray.100"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="gray.500"
    >
      <MessageCircleMore size={24} />
    </Box>
    <Text fontWeight="700" color="gray.900">
      {title}
    </Text>
    <Text fontSize="sm" color="gray.500" maxW="260px">
      {description}
    </Text>
    {action}
  </VStack>
);

export const TeamChatPlaceholder = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <FlexThreadCenter>
    <Avatar size="2xl" bg={teamChatAccent} color="white" name="Team Chat" />
    <Text mt={5} fontSize="xl" fontWeight="800" color="gray.800">
      {title}
    </Text>
    <Text mt={2} fontSize="sm" color="gray.500" maxW="360px" textAlign="center">
      {description}
    </Text>
  </FlexThreadCenter>
);

const FlexThreadCenter = ({ children }: { children: React.ReactNode }) => (
  <Box
    flex={1}
    display={{ base: 'none', md: 'flex' }}
    alignItems="center"
    justifyContent="center"
    bg={teamChatThreadBg}
    position="relative"
    overflow="hidden"
  >
    <VStack spacing={0}>{children}</VStack>
  </Box>
);

export const NewChatButton = ({
  onClick,
}: {
  onClick: () => void;
}) => (
  <IconButton
    aria-label="Start a new chat"
    icon={<AddIcon />}
    bg={teamChatAccent}
    color="white"
    borderRadius="14px"
    _hover={{ bg: '#17664D' }}
    _active={{ bg: '#145A44' }}
    onClick={onClick}
  />
);
